import { ActionReducerMap, createSelector } from '@ngrx/store';
import * as fromProducts from './products.reducer';
import { ProductState } from './products.reducer';
import * as fromAddressSelector from './address-selector.reducer';
import { AddressSelectorState } from './address-selector.reducer';
import * as fromExceedingAmount from './exceeding-amount.reducer';
import { ExceedingAmount } from '@dch/products-business-logic';
import * as fromPricingPlan from './pricing-plan.reducer';
import { PricingPlanState } from './pricing-plan.reducer';
import * as fromMagnoliaConfig from './magnolia-config.reducer';
import { ConfigState } from './magnolia-config.reducer';
import * as fromAlarmThreshold from './alarm-threshold.reducer';
import { AlarmThresholdState } from './alarm-threshold.reducer';
import * as fromProductDetails from './product-details.reducer';
import { ProductDetailsState } from './product-details.reducer';
import * as fromProductSelector from './product-selector.reducer';
import { ProductSelectorState } from './product-selector.reducer';
import * as fromAvailableSpeedSelector from './available-speed.reducer';
import { AvailableSpeedState } from './available-speed.reducer';
import * as fromInternetSettings from './internet-settings.reducer';
import { InternetPasswordState } from './internet-settings.reducer';
import * as fromMailboxes from '../../mymail/store/reducers/mailboxes.reducer';
import * as fromAliases from '../../mymail/store/reducers/aliases.reducer';
import * as fromPassword from '../../mymail/store/reducers/password.reducer';
import * as fromPasswordRecoveryManagement from '../../mymail/store/reducers/password-recovery-management.reducer';
import * as fromNetflixAccountManagement from './netflix-account-management.reducer';
import * as fromDisneyPlusAccountManagement from './disney-plus-account-management.reducer';
import { Mailbox } from '../../mymail/models/mailboxes.model';
import * as fromNortonState from '../../norton/store/reducers/norton.reducer';
import { NortonState } from '../../norton/store/reducers/norton.reducer';
import * as fromCloudState from '../../cloud/store/reducers/cloud.reducer';
import { CloudState } from '../../cloud/store/reducers/cloud.reducer';
import * as fromPublicWifiState from '../../public-wifi/store/reducers/public-wifi.reducer';
import { PublicWifiState } from '../../public-wifi/store/reducers/public-wifi.reducer';
import * as fromPublicWifiPxsplusState from '../../public-wifi-pxsplus/store/reducers/public-wifi-pxsplus.reducer';
import { PublicWifiPxsplusState } from '../../public-wifi-pxsplus/store/reducers/public-wifi-pxsplus.reducer';
import * as fromPayAndGoState from './pay-and-go.reducer';
import { PayAndGoState } from './pay-and-go.reducer';
import * as fromCyberCare from '../../cyber-care/store/reducers/cyber-care.reducer';
import { CyberCareState } from '../../cyber-care/store/reducers/cyber-care.reducer';
import * as fromWifiInformationSelector from './wifi-information.reducer';
import { WifiInformationState } from './wifi-information.reducer';
import * as fromOffers from './offers.reducer';
import { OffersState } from './offers.reducer';
import * as fromNsp from '../../nsp/store/reducers/nsp.reducer';
import { NspState } from '../../nsp/store/reducers/nsp.reducer';
import * as fromSplitUnbilledBalance from './split-unbilled-balance.reducer';
import { SplitUnbilledBalanceState } from './split-unbilled-balance.reducer';
import * as fromEbuOrdering from './ebu-ordering.reducer';
import { EbuOrderingState } from './ebu-ordering.reducer';

export interface ApplicationState {
  productDetails: ProductDetailsState;
  products: ProductState;
  address: AddressSelectorState;
  exceedingAmount: ExceedingAmount[];
  config: ConfigState;
  pricingPlan: PricingPlanState;
  alarmThreshold: AlarmThresholdState;
  deepLinkProduct: ProductSelectorState;
  availableSpeed: AvailableSpeedState;
  wifiInformation: WifiInformationState;
  internetSettings: InternetPasswordState;
  mailboxes: fromMailboxes.MailboxState;
  aliases: fromAliases.AliasesState;
  password: fromPassword.PasswordState;
  recovery: fromPasswordRecoveryManagement.PasswordRecoveryManagementState;
  netflixAccountManagement: fromNetflixAccountManagement.NetflixAccountManagementState;
  disneyPlusAccountManagement: fromDisneyPlusAccountManagement.DisneyPlusAccountManagementState;
  norton: NortonState;
  cloud: CloudState;
  publicWifi: PublicWifiState;
  publicWifiPxsplus: PublicWifiPxsplusState;
  payAndGo: PayAndGoState;
  cyberCare: CyberCareState;
  offers: OffersState;
  nsp: NspState;
  splitUnbilledBalance: SplitUnbilledBalanceState;
  ebuOrdering: EbuOrderingState;
}

export const reducers: ActionReducerMap<ApplicationState> = {
  productDetails: fromProductDetails.reducer,
  products: fromProducts.reducer,
  address: fromAddressSelector.reducer,
  exceedingAmount: fromExceedingAmount.reducer,
  pricingPlan: fromPricingPlan.reducer,
  config: fromMagnoliaConfig.reducer,
  alarmThreshold: fromAlarmThreshold.reducer,
  deepLinkProduct: fromProductSelector.reducer,
  availableSpeed: fromAvailableSpeedSelector.reducer,
  wifiInformation: fromWifiInformationSelector.reducer,
  internetSettings: fromInternetSettings.reducer,
  mailboxes: fromMailboxes.reducer,
  aliases: fromAliases.reducer,
  password: fromPassword.reducer,
  recovery: fromPasswordRecoveryManagement.reducer,
  netflixAccountManagement: fromNetflixAccountManagement.reducer,
  disneyPlusAccountManagement: fromDisneyPlusAccountManagement.reducer,
  norton: fromNortonState.reducer,
  cloud: fromCloudState.reducer,
  publicWifi: fromPublicWifiState.reducer,
  publicWifiPxsplus: fromPublicWifiPxsplusState.reducer,
  payAndGo: fromPayAndGoState.reducer,
  cyberCare: fromCyberCare.reducer,
  offers: fromOffers.reducer,
  nsp: fromNsp.reducer,
  splitUnbilledBalance: fromSplitUnbilledBalance.reducer,
  ebuOrdering: fromEbuOrdering.reducer,
};

// Mailboxes
export const getMailboxesState = (state: ApplicationState) => state.mailboxes;
export const getCustomerId = (state: ApplicationState) => state.mailboxes.cdbId;
export const getMailboxes = createSelector(
  getMailboxesState,
  fromMailboxes.getMailboxEntries,
);
export const getShowCreateMailboxLink = createSelector(
  getMailboxesState,
  fromMailboxes.getShowCreateMailboxLink,
);
export const getSelectedMailbox = createSelector(
  getMailboxesState,
  fromMailboxes.getSelectedMailbox,
);
export const getMailboxesSuccessInfo = createSelector(
  getMailboxesState,
  fromMailboxes.getMailboxesSuccess,
);
export const getMailboxesErrorInfo = createSelector(
  getMailboxesState,
  fromMailboxes.getMailboxesError,
);
export const getOpenedMailbox = createSelector(
  getMailboxesState,
  fromMailboxes.getOpenedMailbox,
);
// Aliases
export const getAliasesState = (state: ApplicationState) => state.aliases;
export const getAliases = createSelector(
  getAliasesState,
  fromAliases.getAliasesEntries,
);
export const getAliasSuccess = createSelector(
  getAliasesState,
  fromAliases.getAliasModificationSuccess,
);
export const getAliasFailure = createSelector(
  getAliasesState,
  fromAliases.getAliasModificationFailure,
);
export const getAliasesForSelectedMailbox = createSelector(
  getAliases,
  getSelectedMailbox,
  (aliases: fromAliases.AliasEntry, mailbox: Mailbox) => {
    if (aliases && mailbox) {
      return aliases[mailbox.mbxId];
    }
  },
);

// Password
export const getPasswordState = (state: ApplicationState) => state.password;
export const getPasswordChangeSuccess = createSelector(
  getPasswordState,
  fromPassword.getChangePasswordSuccess,
);
export const getPasswordChangeError = createSelector(
  getPasswordState,
  fromPassword.getChangePasswordError,
);

export const getPasswordRecoveryState = (state: ApplicationState) =>
  state.recovery;
export const getPasswordRecoveryManagement = createSelector(
  getPasswordRecoveryState,
  fromPasswordRecoveryManagement.getPasswordRecoveryManagement,
);

export const getRecoveryMethodError = createSelector(
  getPasswordRecoveryState,
  fromPasswordRecoveryManagement.getError,
);
export const getRecoveryMethodSuccess = createSelector(
  getPasswordRecoveryState,
  fromPasswordRecoveryManagement.getSuccess,
);

export const getProductDetailsState = (state: ApplicationState) =>
  state.productDetails;
export const getProductsState = (state: ApplicationState) => state.products;
export const getAddressSelectorState = (state: ApplicationState) =>
  state.address;
export const getPricingPlanState = (state: ApplicationState) =>
  state.pricingPlan;
export const getAlarmThresholdState = (state: ApplicationState) =>
  state.alarmThreshold;
export const getConfigSelectorState = (state: ApplicationState) => state.config;
export const getInternetState = (state: ApplicationState) =>
  state.internetSettings;
export const getDeepLinkProductState = (state: ApplicationState) =>
  state.deepLinkProduct;
export const getAvailableSpeedState = (state: ApplicationState) =>
  state.availableSpeed;
export const getWifiInformationState = (state: ApplicationState) =>
  state.wifiInformation;
export const getOffersState = (state: ApplicationState) => state.offers;
export const getNetflixAccountManagementState = (state: ApplicationState) =>
  state.netflixAccountManagement;
export const getDisneyPlusAccountManagementState = (state: ApplicationState) =>
  state.disneyPlusAccountManagement;
export const getNortonState = (state: ApplicationState) => state.norton;
export const getCloudState = (state: ApplicationState) => state.cloud;
export const getPublicWifiState = (state: ApplicationState) => state.publicWifi;
export const getPublicWifiPxsplusState = (state: ApplicationState) =>
  state.publicWifiPxsplus;
export const getPayAndGotate = (state: ApplicationState) => state.payAndGo;
export const getUserProduct = createSelector(
  getProductsState,
  (state: ProductState) => ({
    installationAddresses: fromProducts.getInstallationAddresses(state),
    packs: fromProducts.getPacks(state),
    outOfPacks: fromProducts.getOutOfPack(state),
    extraServices: fromProducts.getExtraServices(state),
    hasProductWithoutAddress: fromProducts.getHasProductWithoutAddress(state),
    firstCoreProduct: fromProducts.getFirstCoreProduct(state),
    loaded: state.loaded,
  }),
);
export const getCyberCareState = (state: ApplicationState) => state.cyberCare;
export const getNspState = (state: ApplicationState) => state.nsp;
export const getSplitUnbilledBalanceState = (state: ApplicationState) =>
  state.splitUnbilledBalance;

export const getProductsEmbeddedErrors = createSelector(
  getProductsState,
  fromProducts.getEmbeddedErrors,
);
export const getProductsTechnicalError = createSelector(
  getProductsState,
  fromProducts.getTechnicalError,
);
export const getProductsRedirectedToDetails = createSelector(
  getProductsState,
  fromProducts.getRedirectedToDetails,
);
export const getSelectedAddress = createSelector(
  getAddressSelectorState,
  fromAddressSelector.getSelectedAddress,
);
export const getSelectedProductDetails = createSelector(
  getProductDetailsState,
  fromProductDetails.getSelectedProductDetails,
);
export const getOptionDetails = createSelector(
  getProductDetailsState,
  fromProductDetails.getOptionDetails,
);
export const getSelectedProductAvailableOptionsLoaded = createSelector(
  getProductDetailsState,
  fromProductDetails.getAvailableOptionsLoaded,
);
export const getSelectedProductAvailableOptionsError = createSelector(
  getProductDetailsState,
  fromProductDetails.getAvailableOptionsError,
);
export const getSelectedProductAvailableSettingsLoaded = createSelector(
  getProductDetailsState,
  fromProductDetails.getAvailableSettingsLoaded,
);
export const getSelectedProductAvailableSettingsError = createSelector(
  getProductDetailsState,
  fromProductDetails.getAvailableSettingsError,
);
export const getSelectedProductAvailableSubscriptions = createSelector(
  getProductDetailsState,
  fromProductDetails.getAvailableSubscriptions,
);
export const getSelectedProductAvailableSubscriptionsLoaded = createSelector(
  getProductDetailsState,
  fromProductDetails.getAvailableSubscriptionsLoaded,
);
export const getDeepLinkProductWrapper = createSelector(
  getProductDetailsState,
  fromProductDetails.getDeepLinkProductWrapper,
);
export const getAllDeepLinkProducts = createSelector(
  getProductDetailsState,
  fromProductDetails.getAllDeepLinkProducts,
);
export const getProductDetailsPending = createSelector(
  getProductDetailsState,
  fromProductDetails.getPending,
);
export const getProductDetailsTechnicalError = createSelector(
  getProductDetailsState,
  fromProductDetails.getTechnicalError,
);
export const getMandateDeleted = createSelector(
  getProductDetailsState,
  fromProductDetails.getMandateDeleted,
);
export const getOptionGroupsConfig = createSelector(
  getConfigSelectorState,
  fromMagnoliaConfig.getOptionGroups,
);
export const getOptionsCategoriesConfig = createSelector(
  getConfigSelectorState,
  fromMagnoliaConfig.getOptionsCategories,
);
export const getSettingsCategoriesConfig = createSelector(
  getConfigSelectorState,
  fromMagnoliaConfig.getSettingsCategories,
);
export const getCtaFilterConfig = createSelector(
  getConfigSelectorState,
  fromMagnoliaConfig.getSettingsCtaFilter,
);
export const getExceedingAmountState = (state: ApplicationState) =>
  state.exceedingAmount;
export const getSimInfo = createSelector(
  getPricingPlanState,
  fromPricingPlan.getSimInfo,
);
export const getSimInfoPending = createSelector(
  getPricingPlanState,
  fromPricingPlan.getPending,
);
export const getSimInfoTechnicalError = createSelector(
  getPricingPlanState,
  fromPricingPlan.getTechnicalError,
);
export const getProductsPending = createSelector(
  getProductsState,
  fromProducts.getPending,
);
export const getAlarmThresholdDeleted = createSelector(
  getAlarmThresholdState,
  fromAlarmThreshold.getThresholdDeactivated,
);
export const getAlarmThresholdActivated = createSelector(
  getAlarmThresholdState,
  fromAlarmThreshold.getThresholdActivated,
);
export const getAlarmThresholdClosed = createSelector(
  getAlarmThresholdState,
  fromAlarmThreshold.getModalClosed,
);
export const getAlarmThresholdUpdated = createSelector(
  getAlarmThresholdState,
  fromAlarmThreshold.getThresholdsUpdated,
);
export const getNewAlarmThresholds = createSelector(
  getAlarmThresholdState,
  fromAlarmThreshold.getNewThresholds,
);
export const getDeepLinkSelectedProduct = createSelector(
  getDeepLinkProductState,
  fromProductSelector.getSelectedProduct,
);
export const getFreeVodConfig = createSelector(
  getConfigSelectorState,
  fromMagnoliaConfig.getFreeVod,
);
export const getAvailableSpeed = createSelector(
  getAvailableSpeedState,
  fromAvailableSpeedSelector.getAvailableSpeed,
);
export const getAvailableSpeedTechnicalError = createSelector(
  getAvailableSpeedState,
  fromAvailableSpeedSelector.getTechnicalError,
);
export const getAvailableSpeedPending = createSelector(
  getAvailableSpeedState,
  fromAvailableSpeedSelector.getPending,
);

export const getInternetSettingsModemSafety = createSelector(
  getInternetState,
  fromInternetSettings.getModemSafetyState,
);

export const getInternetPasswordTechnicalError = createSelector(
  getInternetState,
  fromInternetSettings.getInternetPasswordState,
);

export const getSharedIpState = createSelector(
  getInternetState,
  fromInternetSettings.getSharedIpState,
);

export const getMacIpAddressState = createSelector(
  getInternetState,
  fromInternetSettings.getMacIpAddressState,
);

export const getBizzGuestWifi = createSelector(
  getInternetState,
  fromInternetSettings.getBizzGuestWifi,
);

export const getNetflixToken = createSelector(
  getNetflixAccountManagementState,
  fromNetflixAccountManagement.getNetflixToken,
);

export const getNetflixTokenError = createSelector(
  getNetflixAccountManagementState,
  fromNetflixAccountManagement.getTechnicalError,
);

export const getDisneyPlusToken = createSelector(
  getDisneyPlusAccountManagementState,
  fromDisneyPlusAccountManagement.getDisneyPlusToken,
);

export const getDisneyPlusTokenError = createSelector(
  getDisneyPlusAccountManagementState,
  fromDisneyPlusAccountManagement.getTechnicalError,
);

export const getNortonProductsAttributes = createSelector(
  getNortonState,
  fromNortonState.getProductsAttributes,
);

export const getNortonProductsPrice = createSelector(
  getNortonState,
  fromNortonState.getProductsPrice,
);

export const getProducts = createSelector(
  getProductsState,
  fromProducts.getProducts,
);

export const getNortonAccount = createSelector(
  getNortonState,
  fromNortonState.getNortonAccount,
);

export const getNortonAccountPending = createSelector(
  getNortonState,
  fromNortonState.getNortonAccountPending,
);

export const getNortonAccountTechnicalError = createSelector(
  getNortonState,
  fromNortonState.getNortonAccountTechnicalError,
);

export const getCloudProductsPrice = createSelector(
  getCloudState,
  fromCloudState.getProductsPrice,
);

export const getCloudAccountActive = createSelector(
  getCloudState,
  fromCloudState.getAccountActive,
);

export const getCloudAccountLogin = createSelector(
  getCloudState,
  fromCloudState.getAccountLogin,
);

export const getCloudAccountStatusFetching = createSelector(
  getCloudState,
  fromCloudState.getAccountStatusFetching,
);

export const getCloudAccountStatusFetchedSuccess = createSelector(
  getCloudState,
  fromCloudState.getAccountStatusFetchedSuccess,
);

export const getCloudAccountStatusError = createSelector(
  getCloudState,
  fromCloudState.getAccountStatusError,
);

export const getCloudAccountActivationSuccess = createSelector(
  getCloudState,
  fromCloudState.getAccountActivationSuccess,
);

export const getCloudAccountActivationPending = createSelector(
  getCloudState,
  fromCloudState.getAccountActivationPending,
);

export const getCloudAccountActivationError = createSelector(
  getCloudState,
  fromCloudState.getAccountActivationError,
);

export const getPublicWifiProductsJwt = createSelector(
  getPublicWifiState,
  fromPublicWifiState.getProductsJwt,
);

export const getPublicWifiPxsplusProductsJwt = createSelector(
  getPublicWifiPxsplusState,
  fromPublicWifiPxsplusState.getProductsJwt,
);

export const getPublicWifiPxsplusEligibleProducts = createSelector(
  getPublicWifiPxsplusState,
  fromPublicWifiPxsplusState.getEligibleProducts,
);

export const getPublicWifiPxsplusProductsPending = createSelector(
  getPublicWifiPxsplusState,
  fromPublicWifiPxsplusState.getProductsPending,
);

export const getPublicWifiProductsPending = createSelector(
  getPublicWifiState,
  fromPublicWifiState.getProductsPending,
);

export const getPublicWifiProductsError = createSelector(
  getPublicWifiState,
  fromPublicWifiState.getProductsError,
);

export const getPublicWifiAccounts = createSelector(
  getPublicWifiState,
  fromPublicWifiState.getAccounts,
);

export const getPublicWifiAccountsPending = createSelector(
  getPublicWifiState,
  fromPublicWifiState.getAccountsPending,
);

export const getPublicWifiAccountsError = createSelector(
  getPublicWifiState,
  fromPublicWifiState.getAccountsError,
);

export const getPublicWifiAccountsSuccess = createSelector(
  getPublicWifiState,
  fromPublicWifiState.getAccountsSuccess,
);

export const getPublicWifiPxsplusConfigureURL = createSelector(
  getPublicWifiPxsplusState,
  fromPublicWifiPxsplusState.getConfigureURL,
);

export const getPublicWifiPxsplusConfigureURLPending = createSelector(
  getPublicWifiPxsplusState,
  fromPublicWifiPxsplusState.getConfigureURLPending,
);

export const getPublicWifiPxsplusConfigureURLSuccess = createSelector(
  getPublicWifiPxsplusState,
  fromPublicWifiPxsplusState.getConfigureURLSuccess,
);

export const getPublicWifiPxsplusConfigureURLError = createSelector(
  getPublicWifiPxsplusState,
  fromPublicWifiPxsplusState.getConfigureURLError,
);

export const getPublicWifiCreateAliasPending = createSelector(
  getPublicWifiState,
  fromPublicWifiState.getCreateAliasPending,
);

export const getPublicWifiCreateAliasSuccess = createSelector(
  getPublicWifiState,
  fromPublicWifiState.getCreateAliasSuccess,
);

export const getPublicWifiCreateAliasError = createSelector(
  getPublicWifiState,
  fromPublicWifiState.getCreateAliasError,
);

export const getPublicWifiUpdateAliasPending = createSelector(
  getPublicWifiState,
  fromPublicWifiState.getUpdateAliasPending,
);

export const getPublicWifiUpdateAliasSuccess = createSelector(
  getPublicWifiState,
  fromPublicWifiState.getUpdateAliasSuccess,
);

export const getPublicWifiUpdateAliasError = createSelector(
  getPublicWifiState,
  fromPublicWifiState.getUpdateAliasError,
);

export const getPublicWifiUpdatePasswordPending = createSelector(
  getPublicWifiState,
  fromPublicWifiState.getUpdatePasswordPending,
);

export const getPublicWifiUpdatePasswordSuccess = createSelector(
  getPublicWifiState,
  fromPublicWifiState.getUpdatePasswordSuccess,
);

export const getPublicWifiUpdatePasswordError = createSelector(
  getPublicWifiState,
  fromPublicWifiState.getUpdatePasswordError,
);

export const isUserMsisdnOwner = createSelector(
  getPublicWifiState,
  fromPublicWifiState.isUserOwningMsisdn,
);

export const isPayAndGoOrderSuccess = createSelector(
  getPayAndGotate,
  fromPayAndGoState.getOrderPayAndGoSuccess,
);

export const isPayAndGoOrderModalClosed = createSelector(
  getPayAndGotate,
  fromPayAndGoState.getOrderPayAndGoModalClosed,
);

export const getCyberCareProductsPrice = createSelector(
  getCyberCareState,
  fromCyberCare.getProductsPrice,
);

export const getWifiPrivateAccess24Ghz = createSelector(
  getWifiInformationState,
  fromWifiInformationSelector.getWifiPrivateAccess24Ghz,
);

export const getWifiInformationTechnicalError = createSelector(
  getWifiInformationState,
  fromWifiInformationSelector.getTechnicalError,
);
export const getWifiInformationPending = createSelector(
  getWifiInformationState,
  fromWifiInformationSelector.getPending,
);

export const getCopperOutPhasingOffer = createSelector(
  getOffersState,
  fromOffers.getCopperOutPhasingOffer,
);

export const getOffersTechnicalError = createSelector(
  getOffersState,
  fromOffers.getTechnicalError,
);
export const getOffersPending = createSelector(
  getOffersState,
  fromOffers.getPending,
);

export const getDisneyPlusSelectedProductDetails = createSelector(
  getProductDetailsState,
  fromProductDetails.getDisneyPlusProductDetails,
);

export const getNspProductsInPack = createSelector(
  getNspState,
  fromNsp.getNspProductsInPack,
);
export const getNspProductsInPackPending = createSelector(
  getNspState,
  fromNsp.getNspProductsInPackPending,
);
export const getNspProductsInPackTechnicalError = createSelector(
  getNspState,
  fromNsp.getNspProductsInPackTechnicalError,
);
export const getNspStandaloneProducts = createSelector(
  getNspState,
  fromNsp.getNspStandaloneProducts,
);
export const getNspStandaloneProductsPending = createSelector(
  getNspState,
  fromNsp.getNspStandaloneProductsPending,
);
export const getNspStandaloneTechnicalError = createSelector(
  getNspState,
  fromNsp.getNspStandaloneTechnicalError,
);
export const getNspInternetValues = createSelector(
  getNspState,
  fromNsp.getNspInternetValues,
);
export const getNspMobileValues = createSelector(
  getNspState,
  fromNsp.getNspMobileValues,
);
export const getNspPrices = createSelector(getNspState, fromNsp.getNspPrices);
export const getSplitUnbilledBalance = createSelector(
  getSplitUnbilledBalanceState,
  fromSplitUnbilledBalance.getSplitUnbilledBalance,
);

export const getEbuOrderingState = (state: ApplicationState) =>
  state.ebuOrdering;
export const getEbuOrderingStatus = createSelector(
  getEbuOrderingState,
  fromEbuOrdering.getEbuOrderingStatus,
);

export const getEbuOrderingLockTime = createSelector(
  getEbuOrderingState,
  fromEbuOrdering.getEbuOrderingLockTime,
);

export const getEbuOrderingInput = createSelector(
  getEbuOrderingState,
  fromEbuOrdering.getEbuOrderingInput,
);

export const getEbuOrderingError = createSelector(
  getEbuOrderingState,
  fromEbuOrdering.getEbuOrderingError,
);

export const getEbuOrderingLogs = createSelector(
  getEbuOrderingState,
  fromEbuOrdering.getEbuOrderingLogs,
);

export const isEbuOrdering = createSelector(
  getEbuOrderingState,
  fromEbuOrdering.isEbuOrdering,
);

export const getNspStatisticsProductsWithStatus = createSelector(
  getNspState,
  fromNsp.getNspStatisticsWithStatus,
);
